// Chemin: C:\gestion-copro\src\pages\vigie\MonProfilVigiePage.tsx
// Session 245 : Création — page de consultation et modification des données onboarding
// Session 265 : Ajout section "Ma situation" (toggle occupant/bailleur) en première position
// Session 281 : Nom/prénom éditables (sauvegarde users/{uid} + rechargement local immédiat)
//               Layout max-w-2xl → max-w-4xl pour aligner avec les autres pages VIGIE
// Permet à l'utilisateur de vérifier et corriger toutes les données onboarding :
// copropriété (nom, type, date livraison), syndic, promoteur, lots, tantièmes, dénominateur total

import React, { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  Building2,
  Home,
  Car,
  Warehouse,
  Users,
  Wrench,
  ChevronDown,
  ChevronUp,
  Edit2,
  Check,
  X,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  Loader2,
  ShieldCheck,
  Sparkles,
  Lock,
  UserCircle2
} from 'lucide-react';

// ============================================
// TYPES LOCAUX
// ============================================

interface LotProfil {
  nature: string;
  tantiemes: number | null;
  numeroLot?: string;
  // Session 248 : préserver les clesRepartition posées par SENTINELLE
  clesRepartition?: any[];
  etage?: string;
}

interface ProfilData {
  // Copropriété
  nomCopropriete: string;
  adressePostale?: string;
  coproprieteNeuve: boolean;
  dateLivraison?: string; // "YYYY-MM" ou "YYYY-MM-DD"
  // Syndic
  nomSyndic?: string;
  relationSyndic?: string;
  emailSyndic?: string;
  telephoneSyndic?: string;
  // Promoteur
  nomPromoteur?: string;
  // CS
  csActif?: string;
  // Lots
  lots: LotProfil[];
  totalTantiemes: number | null; // dénominateur total
  // Garanties (calculées ou saisies manuellement)
  garanties?: {
    gpa?: string;
    biennale?: string;
    decennale?: string;
  };
}

// ============================================
// CONSTANTES
// ============================================

const NATURES_LOT = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'habitation', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'parking', label: 'Parking' },
  { value: 'cave', label: 'Cave' },
  { value: 'garage', label: 'Garage' },
  { value: 'box', label: 'Box' },
  { value: 'cellier', label: 'Cellier' },
  { value: 'local_annexe', label: 'Local annexe' },
];

const RELATION_SYNDIC_OPTIONS = [
  { value: 'ok', label: 'Correcte', color: 'text-green-700 bg-green-50 border-green-200' },
  { value: 'lent', label: 'Lent', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'problemes', label: 'Problématique', color: 'text-red-700 bg-red-50 border-red-200' },
  { value: 'nouveau', label: 'Nouveau', color: 'text-blue-700 bg-blue-50 border-blue-200' },
];

const CS_OPTIONS = [
  { value: 'actif', label: 'Actif et présent' },
  { value: 'inactif', label: 'Peu actif' },
  { value: 'absent', label: 'Absent' },
  { value: 'inconnu', label: 'Ne sais pas' },
];

// ============================================
// UTILITAIRES
// ============================================

const getLotIcon = (nature: string) => {
  switch (nature.toLowerCase()) {
    case 'appartement':
    case 'habitation':
    case 'maison': return <Home className="w-4 h-4 text-blue-500" />;
    case 'parking':
    case 'garage':
    case 'box': return <Car className="w-4 h-4 text-gray-500" />;
    case 'cave':
    case 'cellier':
    case 'local_annexe': return <Warehouse className="w-4 h-4 text-gray-500" />;
    default: return <Building2 className="w-4 h-4 text-gray-500" />;
  }
};

const getLotLabel = (nature: string): string => {
  return NATURES_LOT.find(n => n.value === nature.toLowerCase())?.label
    || (nature.charAt(0).toUpperCase() + nature.slice(1));
};

const formatDateLivraison = (dateStr?: string): string => {
  if (!dateStr) return '';
  // Accepter YYYY-MM ou YYYY-MM-DD
  const parts = dateStr.split('-');
  if (parts.length >= 2) {
    const mois = new Date(`${parts[0]}-${parts[1]}-01`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return mois.charAt(0).toUpperCase() + mois.slice(1);
  }
  return dateStr;
};

// ============================================
// SOUS-COMPOSANT : Section pliable
// ============================================

interface SectionProps {
  titre: string;
  icone: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string;
  badgeColor?: string;
}

const Section: React.FC<SectionProps> = ({ titre, icone, children, defaultOpen = true, badge, badgeColor = 'bg-gray-100 text-gray-600' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg text-blue-700">
            {icone}
          </span>
          <span className="font-semibold text-gray-800">{titre}</span>
          {badge && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
              {badge}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================
// SOUS-COMPOSANT : Champ éditable inline
// ============================================

interface ChampEditableProps {
  label: string;
  valeur: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'month';
  onSave: (val: string) => Promise<void>;
  info?: string;
}

const ChampEditable: React.FC<ChampEditableProps> = ({ label, valeur, placeholder, type = 'text', onSave, info }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(valeur);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(valeur);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
        <div className="flex items-center gap-2">
          <input
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center w-8 h-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {info && <p className="text-xs text-gray-400">{info}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-2">
        <span className={`text-sm flex-1 ${valeur ? 'text-gray-800' : 'text-gray-400 italic'}`}>
          {valeur || placeholder || 'Non renseigné'}
        </span>
        <button
          onClick={() => { setDraft(valeur); setEditing(true); }}
          className="flex items-center justify-center w-7 h-7 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
          title="Modifier"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {info && <p className="text-xs text-gray-400">{info}</p>}
    </div>
  );
};

// ============================================
// SOUS-COMPOSANT : Sélecteur éditable
// ============================================

interface SelectEditableProps {
  label: string;
  valeur: string;
  options: { value: string; label: string }[];
  onSave: (val: string) => Promise<void>;
}

const SelectEditable: React.FC<SelectEditableProps> = ({ label, valeur, options, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(valeur);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const displayLabel = options.find(o => o.value === valeur)?.label || valeur || 'Non renseigné';

  if (editing) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
        <div className="flex items-center gap-2">
          <select
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
            autoFocus
          >
            <option value="">— Choisir —</option>
            {options.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center w-8 h-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-2">
        <span className={`text-sm flex-1 ${valeur ? 'text-gray-800' : 'text-gray-400 italic'}`}>
          {displayLabel}
        </span>
        <button
          onClick={() => { setDraft(valeur); setEditing(true); }}
          className="flex items-center justify-center w-7 h-7 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
          title="Modifier"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const MonProfilVigiePage: React.FC = () => {
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();

  const [profil, setProfil] = useState<ProfilData | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // ---- Ma situation : toggle occupant/bailleur ----
  const [estBailleur, setEstBailleur] = useState<boolean>(false);
  const [savingBailleur, setSavingBailleur] = useState(false);

  // ---- Mes informations personnelles ----
  const [adressePerso, setAdressePerso] = useState('');
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  // Session 281 : état local nom/prénom/gsm pour affichage immédiat après sauvegarde
  const [prenomLocal, setPrenomLocal] = useState('');
  const [nomLocal, setNomLocal] = useState('');
  const [gsmLocal, setGsmLocal] = useState('');

  useEffect(() => {
    if (userProfile?.prenom) setPrenomLocal(userProfile.prenom);
    if (userProfile?.nom) setNomLocal(userProfile.nom);
    if (userProfile?.gsm !== undefined) setGsmLocal(userProfile.gsm || '');
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.vigieInfo?.estBailleur !== undefined) {
      setEstBailleur(userProfile.vigieInfo.estBailleur === true);
    }
  }, [userProfile]);

  const toggleBailleur = async (val: boolean) => {
    if (!firebaseUser?.uid) return;
    setSavingBailleur(true);
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        'vigieInfo.estBailleur': val,
        updatedAt: serverTimestamp(),
      });
      setEstBailleur(val);
      showToast('Situation mise à jour.', 'success');
    } catch {
      showToast('Erreur lors de la mise à jour.', 'error');
    } finally {
      setSavingBailleur(false);
    }
  };

  // ---- Chargement userPreferences (adresse personnelle bailleur) ----
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    setLoadingPrefs(true);
    getDoc(doc(db, 'userPreferences', firebaseUser.uid)).then(snap => {
      if (snap.exists()) {
        setAdressePerso(snap.data()?.adresseBailleur || '');
      }
    }).finally(() => setLoadingPrefs(false));
  }, [firebaseUser]);

  // ---- Chargement ----
  useEffect(() => {
    if (!coproprieteId) { setLoading(false); return; }
    chargerProfil();
  }, [coproprieteId]);

  const chargerProfil = async () => {
    if (!coproprieteId) return;
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'coproprietes', coproprieteId));
      if (!snap.exists()) { setLoading(false); return; }
      const d = snap.data();

      // Lots depuis la racine coproprietes (source de vérité depuis session 244)
      const lots: LotProfil[] = Array.isArray(d.lots)
        ? d.lots.map((l: any) => ({
            nature: l.nature || 'lot',
            tantiemes: typeof l.tantiemes === 'number' ? l.tantiemes : null,
            // Session 248 : préserver clesRepartition et etage posés par SENTINELLE
            ...(Array.isArray(l.clesRepartition) ? { clesRepartition: l.clesRepartition } : {}),
            ...(l.etage ? { etage: l.etage } : {}),
            numeroLot: l.numeroLot || l.numero || undefined,
          }))
        : [];

      // Dénominateur — totalTantiemes prioritaire, tantiemesTotal en fallback de lecture (session 244 : zéro fallback numérique)
      const totalTantiemes: number | null =
        typeof d.totalTantiemes === 'number' && d.totalTantiemes > 0 ? d.totalTantiemes
        : typeof d.tantiemesTotal === 'number' && d.tantiemesTotal > 0 ? d.tantiemesTotal
        : null;

      // Nom copropriété
      const nomCopropriete: string =
        (typeof d.nom === 'string' ? d.nom : '')
        || (userProfile?.vigieInfo?.nomCopropriete as string | undefined)
        || '';

      setProfil({
        nomCopropriete,
        adressePostale: buildAdresse(d),
        coproprieteNeuve: d.coproprieteNeuve === true,
        dateLivraison: d.dateLivraison || undefined,
        nomSyndic: d.nomSyndic || undefined,
        relationSyndic: d.relationSyndic || undefined,
        emailSyndic: d.emailSyndic || undefined,
        telephoneSyndic: d.telephoneSyndic || undefined,
        nomPromoteur: d.nomPromoteur || undefined,
        csActif: d.csActif || undefined,
        lots,
        totalTantiemes,
        garanties: d.garanties || undefined,
      });
    } catch (e) {
      console.error('[MonProfilVigiePage] Erreur chargement:', e);
      showToast('Erreur lors du chargement du profil.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildAdresse = (d: any): string | undefined => {
    const ap = d.adressePostale;
    const ar = d.adresse;
    if (ap && typeof ap === 'object' && ap.rue) {
      return [ap.rue, [ap.codePostal, ap.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }
    if (ar && typeof ar === 'object' && ar.rue) {
      return [ar.rue, [ar.codePostal, ar.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }
    if (ar && typeof ar === 'string') return ar;
    return undefined;
  };

  // ---- Sauvegarde générique ----
  const sauvegarder = async (champs: Record<string, any>) => {
    if (!coproprieteId) return;
    await updateDoc(doc(db, 'coproprietes', coproprieteId), {
      ...champs,
      updatedAt: serverTimestamp(),
    });
    await chargerProfil();
    showToast('Modification enregistrée.', 'success');
  };

  const sauvegarderInfosPerso = async (champ: 'gsm' | 'adressePerso' | 'prenom' | 'nom', val: string) => {
    if (!firebaseUser?.uid) return;
    if (champ === 'gsm') {
      await updateDoc(doc(db, 'users', firebaseUser.uid), { gsm: val, updatedAt: serverTimestamp() });
      setGsmLocal(val);
    } else if (champ === 'prenom') {
      // Session 281 : normalisation identique à AuthContext (1ère lettre majuscule)
      const normalized = val.trim().charAt(0).toUpperCase() + val.trim().slice(1).toLowerCase();
      await updateDoc(doc(db, 'users', firebaseUser.uid), { prenom: normalized, updatedAt: serverTimestamp() });
      setPrenomLocal(normalized);
    } else if (champ === 'nom') {
      // Session 281 : normalisation identique à AuthContext (tout en majuscules)
      const normalized = val.trim().toUpperCase();
      await updateDoc(doc(db, 'users', firebaseUser.uid), { nom: normalized, updatedAt: serverTimestamp() });
      setNomLocal(normalized);
    } else {
      await setDoc(doc(db, 'userPreferences', firebaseUser.uid), { adresseBailleur: val }, { merge: true });
      setAdressePerso(val);
    }
    showToast('Modification enregistrée.', 'success');
  };

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ---- Sauvegarde lots ----
  const sauvegarderLots = async (nouveauxLots: LotProfil[]) => {
    if (!coproprieteId) return;
    await updateDoc(doc(db, 'coproprietes', coproprieteId), {
      lots: nouveauxLots.map(l => ({
        nature: l.nature,
        tantiemes: l.tantiemes ?? 0,
        ...(l.numeroLot ? { numeroLot: l.numeroLot } : {}),
        // Session 248 : préserver clesRepartition et etage posés par SENTINELLE
        ...(Array.isArray(l.clesRepartition) ? { clesRepartition: l.clesRepartition } : {}),
        ...(l.etage ? { etage: l.etage } : {}),
      })),
      updatedAt: serverTimestamp(),
    });
    await chargerProfil();
    showToast('Lots mis à jour.', 'success');
  };

  // ============================================
  // ÉDITEUR DE LOTS
  // ============================================

  const [lotsEdit, setLotsEdit] = useState(false);
  const [lotsDraft, setLotsDraft] = useState<LotProfil[]>([]);
  const [lotsSaving, setLotsSaving] = useState(false);
  // Session 248 : modal avertissement avant modification forcée
  const [showLotsWarning, setShowLotsWarning] = useState(false);
  // Détecte si SENTINELLE a posé des clesRepartition
  const sentinelleAGeresLots = profil?.lots.some(l => Array.isArray((l as any).clesRepartition) && (l as any).clesRepartition.length > 0) ?? false;

  const startEditLots = () => {
    setLotsDraft(profil?.lots.map(l => ({ ...l })) || []);
    setLotsEdit(true);
  };

  const cancelEditLots = () => {
    setLotsEdit(false);
  };

  const saveEditLots = async () => {
    setLotsSaving(true);
    try {
      await sauvegarderLots(lotsDraft);
      setLotsEdit(false);
    } finally {
      setLotsSaving(false);
    }
  };

  const addLot = () => {
    setLotsDraft(prev => [...prev, { nature: 'appartement', tantiemes: null }]);
  };

  const removeLot = (idx: number) => {
    setLotsDraft(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLot = (idx: number, champ: keyof LotProfil, val: any) => {
    setLotsDraft(prev => prev.map((l, i) => i === idx ? { ...l, [champ]: val } : l));
  };

  // ---- Calcul quote-part ----
  // Session 281 : || au lieu de ?? — lot.tantiemes peut être 0 (pas null) après update_profil_tantiemes
  // 0 ?? fallback = 0 (fallback ignoré) — 0 || fallback = fallback (correct)
  const totalTantProprio = profil?.lots.reduce((s, l) => s + (l.tantiemes || l.clesRepartition?.[0]?.tantiemes || 0), 0) ?? 0;
  const totalTantCopro = profil?.totalTantiemes;
  const quotePart = totalTantCopro && totalTantCopro > 0
    ? Math.round((totalTantProprio / totalTantCopro) * 10000) / 100
    : null;

  // ============================================
  // RENDU
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!profil) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center text-gray-500">
        <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p>Impossible de charger le profil de votre copropriété.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all
          ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* En-tête */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center justify-center w-10 h-10 bg-blue-900 rounded-xl">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Ma copropriété</h1>
          <p className="text-sm text-gray-500">Vérifiez et corrigez vos informations</p>
        </div>
      </div>

      {/* Bandeau info */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>Ces informations permettent à SENTINELLE de personnaliser ses analyses et courriers. Passez votre souris sur un champ pour le modifier.</span>
      </div>

      {/* ---- SECTION : Ma situation ---- */}
      {/* Session 265 : Toggle occupant/bailleur — écrit vigieInfo.estBailleur sur users/{userId} */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center w-8 h-8 bg-amber-50 rounded-lg text-amber-700">
              <UserCircle2 className="w-4 h-4" />
            </span>
            <div>
              <span className="font-semibold text-gray-800">Ma situation</span>
              <p className="text-xs text-gray-500 mt-0.5">Conditionne les modules disponibles dans votre espace</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => !savingBailleur && toggleBailleur(false)}
              disabled={savingBailleur}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                !estBailleur
                  ? 'border-blue-900 bg-blue-900 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50'
              }`}
            >
              <Home className="w-6 h-6" />
              <span className="text-sm font-semibold">Propriétaire occupant</span>
              <span className={`text-xs text-center ${!estBailleur ? 'text-blue-200' : 'text-gray-400'}`}>
                Je vis dans mon appartement
              </span>
            </button>

            <button
              onClick={() => !savingBailleur && toggleBailleur(true)}
              disabled={savingBailleur}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                estBailleur
                  ? 'border-amber-500 bg-amber-500 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-amber-200 hover:bg-amber-50'
              }`}
            >
              <Building2 className="w-6 h-6" />
              <span className="text-sm font-semibold">Propriétaire bailleur</span>
              <span className={`text-xs text-center ${estBailleur ? 'text-amber-100' : 'text-gray-400'}`}>
                Je loue mon appartement
              </span>
            </button>
          </div>

          {savingBailleur && (
            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Enregistrement...
            </div>
          )}

          <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg text-xs ${
            estBailleur
              ? 'bg-amber-50 border border-amber-200 text-amber-800'
              : 'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
            <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            {estBailleur
              ? "SENTINELLE activera Ma comptabilité avec l'onglet Ma location — charges copro, loyers, quittances et régularisations dans un seul espace."
              : "SENTINELLE activera Ma comptabilité pour le suivi de vos charges de copropriété."}
          </div>
        </div>
      </div>

      {/* ---- SECTION : Mes informations personnelles ---- */}
      <Section titre="Mes informations personnelles" icone={<UserCircle2 className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

          {/* Nom et prénom — éditables (Session 281) */}
          <ChampEditable
            label="Prénom"
            valeur={prenomLocal}
            placeholder="Votre prénom"
            onSave={val => sauvegarderInfosPerso('prenom', val)}
            info="Utilisé pour signer vos courriers."
          />

          <ChampEditable
            label="Nom"
            valeur={nomLocal}
            placeholder="Votre nom de famille"
            onSave={val => sauvegarderInfosPerso('nom', val)}
            info="Utilisé pour signer vos courriers."
          />

          {/* Email — lecture seule */}
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
            <p className="text-sm text-gray-800">{userProfile?.email || 'Non renseigné'}</p>
            <p className="text-xs text-gray-400">Pour modifier l'email, contactez le support.</p>
          </div>

          {/* Téléphone — éditable */}
          <ChampEditable
            label="Téléphone"
            valeur={gsmLocal}
            placeholder="Ex : 06 12 34 56 78"
            type="tel"
            onSave={val => sauvegarderInfosPerso('gsm', val)}
          />

          {/* Adresse personnelle bailleur — éditable, stockée dans userPreferences */}
          {estBailleur && (
            <div className="sm:col-span-2">
              <ChampEditable
                label="Votre adresse personnelle (bailleur)"
                valeur={loadingPrefs ? '...' : adressePerso}
                placeholder="Ex : 12 rue des Lilas, 62000 Arras"
                onSave={val => sauvegarderInfosPerso('adressePerso', val)}
                info="Utilisée automatiquement dans vos quittances de loyer"
              />
            </div>
          )}
        </div>
      </Section>

      {/* ---- SECTION : Ma copropriété ---- */}
      <Section titre="Ma copropriété" icone={<Building2 className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

          <ChampEditable
            label="Nom de la copropriété"
            valeur={profil.nomCopropriete}
            placeholder="Ex : Le Tillia"
            onSave={val => sauvegarder({ nom: val })}
          />

          <ChampEditable
            label="Adresse"
            valeur={profil.adressePostale || ''}
            placeholder="Ex : 12 rue des Acacias, 59000 Lille"
            onSave={val => {
              // Session 281 : adresse stockée comme objet { rue, codePostal, ville } en Firestore.
              // Notation pointée pour ne toucher que les sous-champs sans écraser le reste.
              const parts = val.split(',').map((s: string) => s.trim());
              if (parts.length >= 2) {
                const rue = parts[0];
                const reste = parts.slice(1).join(', ').trim();
                const cpMatch = reste.match(/^(\d{5})\s+(.+)$/);
                if (cpMatch) {
                  return sauvegarder({
                    'adressePostale.rue': rue,
                    'adressePostale.codePostal': cpMatch[1],
                    'adressePostale.ville': cpMatch[2],
                  });
                }
                return sauvegarder({ 'adressePostale.rue': rue, 'adressePostale.ville': reste });
              }
              return sauvegarder({ 'adressePostale.rue': val });
            }}
            info="Adresse complète — ex : 12 rue des Acacias, 59000 Lille"
          />

          {/* Type : neuve ou ancienne */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => sauvegarder({ coproprieteNeuve: true })}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors
                  ${profil.coproprieteNeuve ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
              >
                Neuve (&lt; 10 ans)
              </button>
              <button
                onClick={() => sauvegarder({ coproprieteNeuve: false })}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors
                  ${!profil.coproprieteNeuve ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
              >
                Ancienne
              </button>
            </div>
          </div>

          {profil.coproprieteNeuve && (
            <ChampEditable
              label="Date de livraison"
              valeur={profil.dateLivraison || ''}
              type="month"
              placeholder="AAAA-MM"
              onSave={val => sauvegarder({ dateLivraison: val })}
              info="Sert à calculer les dates de garanties constructeur"
            />
          )}
        </div>
      </Section>

      {/* ---- SECTION : Mon syndic ---- */}
      <Section titre="Mon syndic" icone={<Users className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

          <ChampEditable
            label="Nom du syndic"
            valeur={profil.nomSyndic || ''}
            placeholder="Ex : Foncia, Nexity…"
            onSave={val => sauvegarder({ nomSyndic: val })}
          />

          <ChampEditable
            label="Email"
            valeur={profil.emailSyndic || ''}
            placeholder="Ex : syndic@foncia.com"
            type="email"
            onSave={val => sauvegarder({ emailSyndic: val })}
          />

          <ChampEditable
            label="Téléphone"
            valeur={profil.telephoneSyndic || ''}
            placeholder="Ex : 03 28 00 00 00"
            type="tel"
            onSave={val => sauvegarder({ telephoneSyndic: val })}
          />

          <SelectEditable
            label="Relation avec le syndic"
            valeur={profil.relationSyndic || ''}
            options={RELATION_SYNDIC_OPTIONS}
            onSave={val => sauvegarder({ relationSyndic: val })}
          />
        </div>
      </Section>

      {/* ---- SECTION : Conseil syndical ---- */}
      <Section titre="Conseil syndical" icone={<ShieldCheck className="w-4 h-4" />}>
        <SelectEditable
          label="Présence du conseil syndical"
          valeur={profil.csActif || ''}
          options={CS_OPTIONS}
          onSave={val => sauvegarder({ csActif: val })}
        />
      </Section>

      {/* ---- SECTION : Promoteur (si neuve) ---- */}
      {profil.coproprieteNeuve && (
        <Section titre="Promoteur" icone={<Wrench className="w-4 h-4" />}>
          <ChampEditable
            label="Nom du promoteur"
            valeur={profil.nomPromoteur || ''}
            placeholder="Ex : Bouygues Immobilier"
            onSave={val => sauvegarder({ nomPromoteur: val })}
          />
        </Section>
      )}

      {/* ---- SECTION : Mes lots & tantièmes ---- */}
      <Section
        titre="Mes lots & tantièmes"
        icone={<Home className="w-4 h-4" />}
        badge={quotePart !== null ? `${quotePart}% de la copropriété` : undefined}
        badgeColor="bg-amber-100 text-amber-700"
      >

        {/* Dénominateur total — champ critique */}
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              <strong>Total des tantièmes de la copropriété</strong> — ce chiffre figure dans le règlement de copropriété (souvent 1 000 ou 10 000). Il détermine votre quote-part dans tous les calculs de charges.
            </p>
          </div>
          <ChampEditable
            label="Total tantièmes copropriété (dénominateur)"
            valeur={profil.totalTantiemes !== null ? String(profil.totalTantiemes) : ''}
            placeholder="Ex : 1000"
            onSave={async val => {
              const n = parseInt(val, 10);
              if (!isNaN(n) && n > 0) {
                await sauvegarder({ totalTantiemes: n });
              } else {
                showToast('Valeur invalide — entrez un nombre entier positif.', 'error');
              }
            }}
            info={totalTantCopro ? `Actuellement : ${totalTantCopro} tantièmes au total` : 'Non renseigné — la quote-part ne peut pas être calculée'}
          />
        </div>

        {/* Liste des lots */}
        {!lotsEdit ? (
          <>
            {profil.lots.length === 0 ? (
              <p className="text-sm text-gray-400 italic mb-3">Aucun lot renseigné.</p>
            ) : (
              <div className="space-y-2 mb-3">
                {profil.lots.map((lot, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex items-center justify-center w-8 h-8 bg-white rounded-lg shadow-sm border border-gray-100">
                      {getLotIcon(lot.nature)}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        {getLotLabel(lot.nature)}
                        {lot.numeroLot && <span className="text-gray-400 font-normal"> — N° {lot.numeroLot}</span>}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(() => {
                          const t = lot.tantiemes || lot.clesRepartition?.[0]?.tantiemes || null;
                          return t !== null ? `${t} tantièmes` : 'Tantièmes non renseignés';
                        })()}
                      </p>
                    </div>
                    {(() => {
                      const t = lot.tantiemes || lot.clesRepartition?.[0]?.tantiemes || null;
                      return t !== null && totalTantCopro ? (
                        <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          {Math.round((t / totalTantCopro) * 10000) / 100}%
                        </span>
                      ) : null;
                    })()}
                  </div>
                ))}
              </div>
            )}

            {/* Résumé quote-part */}
            {profil.lots.length > 0 && (
              <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                <span>Total mes tantièmes : <strong className="text-gray-800">{totalTantProprio}</strong></span>
                {quotePart !== null && (
                  <span className="text-amber-700">→ <strong>{quotePart}%</strong> de la copropriété</span>
                )}
              </div>
            )}

            {/* Session 248 : Bouton conditionnel selon gestion SENTINELLE ou manuelle */}
            {sentinelleAGeresLots ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Sparkles className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <p className="text-xs text-indigo-700">
                    <strong>Géré par SENTINELLE</strong> — Tes tantièmes ont été extraits depuis ton règlement de copropriété. Pour les modifier, redemande à SENTINELLE d'analyser ton règlement.
                  </p>
                </div>
                <button
                  onClick={() => setShowLotsWarning(true)}
                  className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Lock className="w-3 h-3" />
                  Forcer la modification manuelle
                </button>
              </div>
            ) : (
              <button
                onClick={startEditLots}
                className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors font-medium"
              >
                <Edit2 className="w-3.5 h-3.5" />
                Modifier mes lots
              </button>
            )}

            {/* Modal avertissement modification forcée */}
            {showLotsWarning && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md mx-4">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Attention — modification manuelle</p>
                      <p className="text-sm text-gray-600">
                        Tes tantièmes ont été extraits par SENTINELLE depuis ton règlement de copropriété. 
                        Une modification manuelle <strong>remplacera les clés de répartition détaillées</strong> utilisées pour calculer tes charges récupérables.
                      </p>
                      <p className="text-sm text-amber-700 mt-2 font-medium">
                        Pour corriger une erreur, il est préférable de demander à SENTINELLE de réanalyser ton règlement.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowLotsWarning(false)}
                      className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => { setShowLotsWarning(false); startEditLots(); }}
                      className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                    >
                      Modifier quand même
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Mode édition lots */
          <div className="space-y-3">
            {lotsDraft.map((lot, i) => (
              <div key={i} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">

                  {/* Nature */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Type de lot</label>
                    <select
                      value={lot.nature}
                      onChange={e => updateLot(i, 'nature', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-400"
                    >
                      {NATURES_LOT.map(n => (
                        <option key={n.value} value={n.value}>{n.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Numéro lot */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">N° lot (optionnel)</label>
                    <input
                      type="text"
                      value={lot.numeroLot || ''}
                      onChange={e => updateLot(i, 'numeroLot', e.target.value || undefined)}
                      placeholder="Ex : A12"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                    />
                  </div>

                  {/* Tantièmes */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Tantièmes</label>
                    <input
                      type="number"
                      value={lot.tantiemes ?? ''}
                      onChange={e => {
                        const n = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        updateLot(i, 'tantiemes', isNaN(n as number) ? null : n);
                      }}
                      placeholder="Ex : 120"
                      min={0}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                    />
                  </div>
                </div>

                <button
                  onClick={() => removeLot(i)}
                  className="mt-5 flex items-center justify-center w-7 h-7 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Supprimer ce lot"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            <button
              onClick={addLot}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white hover:bg-gray-50 border border-dashed border-gray-300 rounded-lg transition-colors w-full justify-center"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter un lot
            </button>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={saveEditLots}
                disabled={lotsSaving}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {lotsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Enregistrer
              </button>
              <button
                onClick={cancelEditLots}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </Section>

    </div>
  );
};

export default MonProfilVigiePage;
